import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { AlertTriangle, DollarSign, FileWarning, DatabaseZap } from 'lucide-react';

export const Slide02Problem: React.FC = () => {
  return (
    <DeckSlideWrapper
      slideNumber={2}
      totalSlides={12}
      category="The Problem"
      title="The Enterprise AI Paradox: Adoption vs. Risk"
      subtitle="Enterprises desperately need Generative AI to stay competitive, but CISOs & CFOs are forced to block it."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {/* Pain Point 1 */}
        <div className="flex flex-col justify-between p-4 sm:p-5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-red-500/30 dark:border-red-500/30 shadow-sm relative overflow-hidden group hover:border-red-500/50 transition-all">
          <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full blur-xl pointer-events-none" />
          <div>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-red-500/10 text-red-600 dark:text-red-500 flex items-center justify-center mb-3 sm:mb-4">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="font-mono text-[11px] sm:text-xs text-red-600 dark:text-red-400 font-bold uppercase tracking-wider mb-1">
              Risk 01 // Security &amp; IP
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1.5 sm:mb-2">
              Uncontrolled Data Leakage
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Employees paste PII, financial records, internal source code, and API keys directly into public LLMs. Once sent upstream, sensitive tokens are stored and trained on.
            </p>
          </div>
          <div className="mt-3.5 pt-2.5 sm:pt-3 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[11px] sm:text-xs text-red-700 dark:text-red-400 font-semibold flex items-center gap-1.5">
            <span>78%</span> of CISOs restrict or ban raw employee AI usage.
          </div>
        </div>

        {/* Pain Point 2 */}
        <div className="flex flex-col justify-between p-4 sm:p-5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-amber-500/30 dark:border-amber-500/30 shadow-sm relative overflow-hidden group hover:border-amber-500/50 transition-all">
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full blur-xl pointer-events-none" />
          <div>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-500 flex items-center justify-center mb-3 sm:mb-4">
              <DollarSign className="w-5 h-5" />
            </div>
            <div className="font-mono text-[11px] sm:text-xs text-amber-600 dark:text-amber-400 font-bold uppercase tracking-wider mb-1">
              Risk 02 // Financial
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1.5 sm:mb-2">
              Runaway, Uncapped API Spend
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              LLM billing is non-deterministic. A runaway agent or single loop can cost $10k+ overnight. Finance teams have zero per-department real-time USD caps or overdraft guardrails.
            </p>
          </div>
          <div className="mt-3.5 pt-2.5 sm:pt-3 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[11px] sm:text-xs text-amber-700 dark:text-amber-400 font-semibold flex items-center gap-1.5">
            <span>3.4x</span> avg monthly budget overshoot on LLM APIs.
          </div>
        </div>

        {/* Pain Point 3 */}
        <div className="flex flex-col justify-between p-4 sm:p-5 rounded-2xl bg-white/90 dark:bg-navy-850/80 border border-blue-500/30 dark:border-blue-500/30 shadow-sm relative overflow-hidden group hover:border-blue-500/50 transition-all">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full blur-xl pointer-events-none" />
          <div>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-500 flex items-center justify-center mb-3 sm:mb-4">
              <FileWarning className="w-5 h-5" />
            </div>
            <div className="font-mono text-[11px] sm:text-xs text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider mb-1">
              Risk 03 // Governance
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1.5 sm:mb-2">
              Compliance &amp; Vendor Lock-In
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              GDPR, HIPAA, and the EU AI Act require strict data auditability and residency. Switching between OpenAI, Anthropic, and local open-source models requires complex code refactors.
            </p>
          </div>
          <div className="mt-3.5 pt-2.5 sm:pt-3 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[11px] sm:text-xs text-blue-700 dark:text-blue-400 font-semibold flex items-center gap-1.5">
            <span>$4.45M</span> avg total cost of an enterprise compliance breach.
          </div>
        </div>
      </div>

      {/* Summary Banner */}
      <div className="mt-4 sm:mt-5 p-3 sm:p-3.5 rounded-xl bg-slate-900 dark:bg-navy-950 text-slate-200 border border-slate-700 dark:border-navy-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs font-mono">
        <div className="flex items-center gap-2">
          <DatabaseZap className="w-4 h-4 text-gold-400 shrink-0" />
          <span className="text-slate-300">
            Enterprise dilemma: <strong className="text-white">Block AI &amp; lose advantage</strong> vs. <strong className="text-white">Deploy AI &amp; risk multi-million dollar data breaches</strong>.
          </span>
        </div>
        <span className="inline-block px-2 py-0.5 rounded bg-gold-500/20 text-gold-400 text-[10px] sm:text-[11px] font-bold shrink-0">
          WORKOUSE RESOLVES BOTH
        </span>
      </div>
    </DeckSlideWrapper>
  );
};
